import _ from "lodash";
import React, { useState, useEffect } from "react";
import firebase from "firebase/app";
import "firebase/auth";
import clsx from "clsx";
import { makeStyles } from "@material-ui/styles";
import {
  Card,
  CardActions,
  CardContent,
  CardHeader,
  colors,
  Divider,
  Button,
  Modal,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@material-ui/core";
import { fade } from "@material-ui/core/styles/colorManipulator";
import CloseIcon from "@material-ui/icons/Close";
import { useAlert } from "react-alert";
import PerfectScrollbar from "react-perfect-scrollbar";
import PropTypes from "prop-types";
import moment from "moment-timezone";
import * as Sentry from "@sentry/browser";

import AddIcon from "@material-ui/icons/AddOutlined";

import Navigation from "./Navigation";
import { makeRequest } from "./util";
import Label from "./Label.js";

const useTableStyles = makeStyles((theme) => ({
  content: {
    padding: 0,
    "&:last-child": {
      paddingBottom: 0,
    },
  },
  inner: {
    minWidth: 700,
  },
  hidden: {
    visibility: "hidden",
  },
  card: {
    width: "65%",
    minWidth: 800,
  },
  clickableImg: {
    "&:hover": {
      cursor: "pointer",
    },
  },
  navigation: {
    paddingLeft: 0,
    paddingRight: 0,
  },
  bold: {
    fontWeight: 550,
  },
  deleteEventButton: {
    color: colors.red[600],
    border: `1px solid ${fade(colors.red[600], 0.5)}`,
    "&:hover": {
      border: `1px solid ${colors.red[600]}`,
      backgroundColor: fade(colors.red[600], theme.palette.action.hoverOpacity),
      // Reset on touch devices, it doesn't add specificity
      "@media (hover: none)": {
        backgroundColor: "transparent",
      },
    },
  },
  topbar: {
    display: "flex",
    backgroundColor: "white",
    width: "100%",
    padding: "10px",
    justifyContent: "flex-end",
  },
  body: {
    paddingLeft: 60,
    paddingTop: 45,
  },
  member: {
    "&:hover": {
      backgroundColor: "rgba(38, 50, 56, 0.08)",
    },
  },
}));

function TeamPage() {
  const classes = useTableStyles();
  const alert = useAlert();

  const [members, setMembers] = useState(null);
  const [inviteUserEmail, setInviteUserEmail] = useState(null);
  const [userToDelete, setUserToDelete] = useState(null);

  function handleInviteUser() {
    makeRequest("invite_user", { email: inviteUserEmail })
      .then(() => {
        setInviteUserEmail(null);
        alert.success("Invite Sent");
      })
      .catch((error) => {
        alert.error("Something went wrong. Unable to load page.");
        console.log(error);
        Sentry.captureException(error);
      });
  }

  function handleDeleteUser() {
    makeRequest("remove_user", { uid: userToDelete.uid })
      .then(() => {
        setMembers((members) => {
          return members.filter((member) => member.uid !== userToDelete.uid);
        });
        setUserToDelete(null);
      })
      .catch((error) => {
        alert.error("Something went wrong. Unable to load page.");
        console.log(error);
        Sentry.captureException(error);
      });
  }

  useEffect(() => {
    makeRequest("list_team_members")
      .then((response) => {
        const you = response.data.filter(
          (user) => user.email === firebase.auth().currentUser.email
        );
        const otherUsers = _(response.data)
          .filter((user) => user.email !== firebase.auth().currentUser.email)
          .sortBy((user) => user.email)
          .value();

        setMembers(you.concat(otherUsers));
      })
      .catch((error) => {
        alert.error("Something went wrong. Unable to load page.");
        console.log(error);
        Sentry.captureException(error);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <div className={clsx(classes.topbar)}>
        <Button
          color="primary"
          variant="contained"
          onClick={() => setInviteUserEmail("")}
        >
          <AddIcon className={classes.icon} />
          ADD TEAMMATE
        </Button>
      </div>
      {members && (
        <MemberList members={members} onClickDelete={setUserToDelete} />
      )}
      <InviteUserModal
        open={inviteUserEmail !== null}
        onClose={() => setInviteUserEmail(null)}
        onInvite={handleInviteUser}
        onChange={setInviteUserEmail}
      />
      <DeleteUserModal
        open={userToDelete !== null}
        user={userToDelete}
        onClose={() => setUserToDelete(null)}
        onDelete={handleDeleteUser}
      />
    </>
  );
}

const MemberList = React.memo(function MemberList({ members, onClickDelete }) {
  const classes = useTableStyles();

  return (
    <div className={clsx(classes.body)}>
      <Card className={clsx(classes.card)}>
        <CardHeader title="Teammates" />
        <Divider />
        <CardContent className={clsx(classes.content)}>
          <PerfectScrollbar>
            <div className={clsx(classes.inner)}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell />
                    <TableCell>Email</TableCell>
                    <TableCell align="center">Created At</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {members.map((member) => (
                    <TableRow key={member.email} className={classes.member}>
                      <TableCell>
                        {member.email === firebase.auth().currentUser.email && (
                          <Label color="#3f51b5">You</Label>
                        )}
                      </TableCell>
                      <TableCell>
                        <span className={clsx(classes.bold)}>
                          {member.email}
                        </span>
                      </TableCell>
                      <TableCell align="center">
                        {moment(member.created_at).format("MMMM D, YYYY")}
                      </TableCell>
                      <TableCell align="center">
                        <Button
                          className={clsx(
                            classes.deleteEventButton,
                            member.email ===
                              firebase.auth().currentUser.email &&
                              classes.hidden
                          )}
                          onClick={() => onClickDelete(member)}
                        >
                          Delete
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </PerfectScrollbar>
        </CardContent>
      </Card>
    </div>
  );
});

MemberList.propTypes = {
  members: PropTypes.array.isRequired,
  onClickDelete: PropTypes.func.isRequired,
};

const useModalStyles = makeStyles((theme) => ({
  root: {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    outline: "none",
    boxShadow: theme.shadows[20],
    width: 500,
    maxHeight: "100%",
    overflowY: "auto",
    maxWidth: "100%",
  },
  container: {
    marginTop: theme.spacing(3),
    height: 200,
  },
  actions: {
    justifyContent: "flex-end",
  },
  inputField: {
    marginTop: 16,
    overflowX: "wrap",
    wordBreak: "break-all",
  },
  deactivateButton: {
    color: theme.palette.common.white,
    backgroundColor: colors.red[600],
    "&:hover": {
      backgroundColor: colors.red[900],
      // Reset on touch devices, it doesn't add specificity
      "@media (hover: none)": {
        backgroundColor: colors.red[600],
      },
    },
  },
  closeIconAction: {
    "&:hover": {
      cursor: "pointer",
    },
    margin: 0,
  },
  bold: {
    fontWeight: 550,
  },
}));

function InviteUserModal({ open, onClose, onChange, onInvite }) {
  const classes = useModalStyles();

  return (
    <Modal onClose={onClose} open={open}>
      <Card className={clsx(classes.root)}>
        <CardHeader
          title={"Invite Teammate"}
          classes={{ action: clsx(classes.closeIconAction) }}
          action={<CloseIcon onClick={onClose} />}
        />
        <Divider />
        <CardContent>
          <TextField
            fullWidth
            id="invite-user-email"
            label="Email"
            name="invite-user-email"
            variant="outlined"
            className={clsx(classes.inputField)}
            onChange={(e) => onChange(e.target.value)}
          />
        </CardContent>
        <Divider />
        <CardActions className={classes.actions}>
          <Button onClick={onClose}>Close</Button>
          <Button color="primary" onClick={onInvite} variant="contained">
            Send Invite
          </Button>
        </CardActions>
      </Card>
    </Modal>
  );
}

InviteUserModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onInvite: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
};

function DeleteUserModal({ open, user, onClose, onDelete }) {
  const classes = useModalStyles();

  const userString = user ? user.email : "this user";

  return (
    <Modal onClose={onClose} open={open}>
      <Card className={clsx(classes.root)}>
        <CardHeader
          title={"Invite Teammate"}
          classes={{ action: clsx(classes.closeIconAction) }}
          action={<CloseIcon onClick={onClose} />}
        />
        <Divider />
        <CardContent>
          <Typography variant="body1">
            <span className={clsx(classes.bold)}>
              Are you sure you want to delete {userString} from your account?
            </span>
          </Typography>
        </CardContent>
        <Divider />
        <CardActions className={classes.actions}>
          <Button onClick={onClose}>Close</Button>
          <Button
            className={classes.deactivateButton}
            onClick={onDelete}
            variant="contained"
          >
            Delete User
          </Button>
        </CardActions>
      </Card>
    </Modal>
  );
}

DeleteUserModal.propTypes = {
  open: PropTypes.bool.isRequired,
  user: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
};

const navigationStyles = makeStyles(() => ({
  navigation: {
    paddingLeft: 0,
    paddingRight: 0,
  },
}));

function TeamPageWithNavigation() {
  const classes = navigationStyles();

  return (
    <Navigation className={classes.navigation}>
      <TeamPage />
    </Navigation>
  );
}

export default TeamPageWithNavigation;
